import {
  ClassroomTeacherCallList,
  Group,
  GroupMember,
  GroupPayload,
  Message,
  MessagePayload,
  Res,
  StudentCalllist,
  UpdateMessagePayload,
} from '@/types'

import base from './base'

export const messageApi = base.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getClassroomSubjectContacts: builder.query<StudentCalllist[], string>({
      query: (id) => `teachers/classroom-subjects/${id}/calllist`,
      transformResponse: (res: Res<StudentCalllist[]>) => res.data,
      keepUnusedDataFor: 300,
    }),

    getChatClassroomContacts: builder.query<ClassroomTeacherCallList[], void>({
      query: () => 'teachers/classroom-teachers/calllists',
      transformResponse: (res: Res<ClassroomTeacherCallList[]>) => res.data,
    }),

    getGroups: builder.query<Group[], void>({
      query: () => 'groups',
      transformResponse: (res: Res<Group[]>) => res.data,
      providesTags: ['Group'],
    }),

    getGroupMessages: builder.query<Message[], number>({
      query: (id) => ({
        url: `groups/${id}/messages`,
      }),
      transformResponse: (res: Res<Message[]>) => res.data,
      providesTags: ['Message'],
    }),

    addGroup: builder.mutation<Group, GroupPayload>({
      query: (body) => ({
        url: 'groups',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Group'],
      transformResponse: (res: Res<Group>) => res.data,
    }),

    addMessage: builder.mutation<Message, MessagePayload>({
      query: ({ groupId, ...body }) => ({
        url: `groups/${groupId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Group', 'Message'],
      transformResponse: (res: Res<Message>) => res.data,
    }),

    markMessageAsRead: builder.mutation<Message, number>({
      query: (id) => ({
        url: `messages/${id}/read`,
        method: 'PUT',
      }),
      invalidatesTags: ['Group', 'Home', 'Message'],
    }),

    removeGroup: builder.mutation<void, number>({
      query: (id) => ({
        url: `groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (!error ? ['Group', 'Home'] : []),
    }),

    groupMembers: builder.query<GroupMember[], number>({
      query: (id) => `groups/${id}/members`,
      transformResponse: (res: Res<GroupMember[]>) => res.data,
    }),

    readForGroup: builder.mutation<void, number>({
      query: (id) => ({
        url: `groups/${id}/read`,
        method: 'PUT',
      }),
      invalidatesTags: (_, error) => (!error ? ['Group', 'Home'] : []),
    }),

    updateMessage: builder.mutation<void, UpdateMessagePayload>({
      query: ({ id, ...body }) => ({
        url: `messages/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error) => (!error ? ['Message'] : []),
    }),

    deleteMessage: builder.mutation<void, number>({
      query: (id) => ({
        url: `messages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (!error ? ['Message'] : []),
    }),
  }),
})

export const {
  useGetGroupsQuery,
  useAddGroupMutation,
  useGroupMembersQuery,
  useAddMessageMutation,
  useRemoveGroupMutation,
  useReadForGroupMutation,
  useGetGroupMessagesQuery,
  useUpdateMessageMutation,
  useDeleteMessageMutation,
  useMarkMessageAsReadMutation,
  useGetChatClassroomContactsQuery,
  useGetClassroomSubjectContactsQuery,
  useLazyGetClassroomSubjectContactsQuery,
} = messageApi
